import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node, mergeAttributes, type JSONContent } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import TestQuestionChoiceNodeView from './test-question-choice-node-view';
import TestQuestionInstructionsNodeView from './test-question-instructions-node-view';
import TestQuestionNodeView from './test-question-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        testQuestion: {
            insertTestQuestion: (options?: InsertTestQuestionOptions) => ReturnType;
            addTestQuestionChoice: (testQuestionPos: number) => ReturnType;
        };
    }
}

export interface InsertTestQuestionOptions {
    testQuestionId?: number | null;
    id?: string | null;
    gdocId?: string | null;
    showInEval?: boolean;
    choices?: Array<{
        text?: string;
        isCorrect?: boolean;
    }>;
}

const defaultTestQuestionNode = (options: InsertTestQuestionOptions = {}): JSONContent => {
    const choices = options.choices ?? [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
    ];

    const choiceNodes = choices.map(choice => ({
        type: 'testQuestionChoice',
        attrs: { isCorrect: choice.isCorrect ?? false },
        content: [
            {
                type: 'paragraph',
                content: choice.text ? [{ type: 'text', text: choice.text }] : undefined,
            },
        ],
    }));

    return {
        type: 'testQuestion',
        attrs: {
            id: options.id ?? null,
            testQuestionId: options.testQuestionId ?? null,
            gdocId: options.gdocId ?? null,
            showInEval: options.showInEval ?? false,
        },
        content: [
            {
                type: 'testQuestionInstructions',
                content: [{ type: 'paragraph' }],
            },
            ...choiceNodes,
        ],
    };
};

const defaultTestQuestionChoiceNode = (): JSONContent => ({
    type: 'testQuestionChoice',
    attrs: { isCorrect: false },
    content: [{ type: 'paragraph' }],
});

export const TestQuestionNodeHeadless = Node.create({
    name: 'testQuestion',

    group: 'block',

    content: 'testQuestionInstructions testQuestionChoice+',

    defining: true,

    isolating: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-test-question-id'),
                renderHTML: attributes => ({
                    'data-test-question-id': attributes.id,
                }),
            },
            testQuestionId: {
                default: null,
                parseHTML: element => {
                    const value = element.getAttribute('data-test-question-db-id');
                    return value ? Number(value) : null;
                },
                renderHTML: attributes => ({
                    'data-test-question-db-id': attributes.testQuestionId,
                }),
            },
            gdocId: {
                default: null,
                parseHTML: element => element.getAttribute('data-test-question-gdoc-id'),
                renderHTML: attributes => ({
                    'data-test-question-gdoc-id': attributes.gdocId,
                }),
            },
            showInEval: {
                default: false,
                parseHTML: element => element.getAttribute('data-show-in-eval') === 'true',
                renderHTML: attributes => ({
                    'data-show-in-eval': attributes.showInEval ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="test-question"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'test-question',
                class: 'test-question-container',
            }),
            0,
        ];
    },

    addCommands() {
        return {
            insertTestQuestion:
                (options: InsertTestQuestionOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent(defaultTestQuestionNode(options));
                },
            addTestQuestionChoice:
                (testQuestionPos: number) =>
                ({ chain, state }) => {
                    const testQuestionNode = state.doc.nodeAt(testQuestionPos);
                    if (!testQuestionNode || testQuestionNode.type.name !== 'testQuestion') {
                        return false;
                    }

                    const insertPos = testQuestionPos + testQuestionNode.nodeSize - 1;

                    return chain()
                        .focus()
                        .insertContentAt(insertPos, defaultTestQuestionChoiceNode())
                        .selectDescendantNodeContent('testQuestionChoice', testQuestionPos)
                        .run();
                },
        };
    },
});

export const TestQuestionInstructionsNodeHeadless = Node.create({
    name: 'testQuestionInstructions',

    group: 'testQuestionContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-test-question-instructions-id'),
                renderHTML: attributes => ({
                    'data-test-question-instructions-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="test-question-instructions"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'test-question-instructions',
            }),
            0,
        ];
    },
});

export const TestQuestionChoiceNodeHeadless = Node.create({
    name: 'testQuestionChoice',

    group: 'testQuestionContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    isolating: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-test-question-choice-id'),
                renderHTML: attributes => ({
                    'data-test-question-choice-id': attributes.id,
                }),
            },
            isCorrect: {
                default: false,
                parseHTML: element => element.getAttribute('data-correct') === 'true',
                renderHTML: attributes => ({
                    'data-correct': attributes.isCorrect ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="test-question-choice"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'test-question-choice',
            }),
            0,
        ];
    },
});

export const TestQuestionChoiceNode = TestQuestionChoiceNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(TestQuestionChoiceNodeView);
    },
});

export const TestQuestionInstructionsNode = TestQuestionInstructionsNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(TestQuestionInstructionsNodeView);
    },
});

export const TestQuestionNode = TestQuestionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(TestQuestionNodeView, { trackNodeViewPosition: true });
    },
});
